const DEFAULT_API_BASE = "https://api-iam.intercom.io";
const SCRIPT_ID = "finu-intercom-widget";

let loadPromise = null;
let isBooted = false;
let isReady = false;
let bootPromise = null;
let bootGeneration = 0;
let lastBootSignature = null;
const READY_TIMEOUT_MS = 12000;
const SCRIPT_TIMEOUT_MS = 12000;

const getWindow = () => (typeof window === "undefined" ? null : window);

const getDocument = () => (typeof document === "undefined" ? null : document);

const getAppId = () => import.meta.env.VITE_INTERCOM_APP_ID?.trim() || "";

const getApiBase = () =>
  import.meta.env.VITE_INTERCOM_API_BASE?.trim() || DEFAULT_API_BASE;

export const getIntercomIdentity = (user) => {
  if (!user) return null;

  const userId =
    user.id ||
    user.user_id ||
    user.userId ||
    user.wallet_address ||
    user.address ||
    user.email;
  if (!userId) return null;

  const createdAtRaw =
    user.created_at ||
    user.createdAt ||
    user.signed_up_at ||
    user.signedUpAt ||
    user.signupAt;
  const createdAt = createdAtRaw ? Number(createdAtRaw) : null;
  const name = `${user.firstName || user.first_name || ""} ${
    user.lastName || user.last_name || ""
  }`.trim();

  return {
    user_id: String(userId),
    email: user.email || undefined,
    name: name || undefined,
    created_at: Number.isFinite(createdAt) ? createdAt : undefined,
    page_title: typeof document !== "undefined" ? document.title : undefined,
    custom_attributes: {
      finu_platform: "web",
      finu_plan: user.tier || user.plan_tier || user.subscription || undefined,
      finu_kyc_status:
        typeof user.isKyc === "boolean"
          ? user.isKyc
          : typeof user.kyc_status === "boolean"
            ? user.kyc_status
            : undefined,
    },
  };
};

const ensureStub = () => {
  const win = getWindow();
  if (!win) return;
  if (typeof win.Intercom === "function") return;

  const intercom = function intercomStub() {
    intercom.c(arguments);
  };

  intercom.q = [];
  intercom.c = function capture(args) {
    intercom.q.push(args);
  };

  win.Intercom = intercom;
};

const loadScript = (appId) => {
  const win = getWindow();
  const doc = getDocument();
  if (!win || !doc || !appId) return Promise.resolve(false);

  if (win.__finuIntercomScriptLoaded) return Promise.resolve(true);
  if (loadPromise) return loadPromise;

  ensureStub();

  loadPromise = new Promise((resolve, reject) => {
    // A previous failed load can leave its script element behind. Its presence
    // does not mean the widget executed successfully.
    doc.getElementById(SCRIPT_ID)?.remove();

    const script = doc.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    let settled = false;
    const fail = (message) => {
      if (settled) return;
      settled = true;
      win.clearTimeout(timer);
      script.remove();
      win.__finuIntercomScriptLoaded = false;
      loadPromise = null;
      reject(new Error(message));
    };
    const timer = win.setTimeout(
      () => fail("Intercom widget load timed out"),
      SCRIPT_TIMEOUT_MS,
    );
    script.onload = () => {
      if (settled) return;
      settled = true;
      win.clearTimeout(timer);
      win.__finuIntercomScriptLoaded = true;
      resolve(true);
    };
    script.onerror = () => fail("Failed to load Intercom widget");

    const firstScript = doc.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      doc.head.appendChild(script);
    }
  });

  return loadPromise;
};

const buildSignature = (settings) =>
  JSON.stringify([
    settings?.app_id || "",
    settings?.user_id || "",
    settings?.email || "",
    settings?.name || "",
    settings?.created_at || "",
  ]);

const buildSettings = (settings = {}) => {
  const appId = settings.app_id || getAppId();
  if (!appId) return null;

  const nextSettings = {
    api_base: getApiBase(),
    app_id: appId,
    hide_default_launcher: true,
    hide_notifications: true,
  };

  if (settings.user_id) nextSettings.user_id = settings.user_id;
  if (settings.email) nextSettings.email = settings.email;
  if (settings.name) nextSettings.name = settings.name;
  if (settings.created_at) nextSettings.created_at = settings.created_at;
  if (settings.page_title) nextSettings.page_title = settings.page_title;
  if (settings.language_override) {
    nextSettings.language_override = settings.language_override;
  }

  if (settings.avatar) nextSettings.avatar = settings.avatar;
  if (settings.company) nextSettings.company = settings.company;
  if (settings.custom_attributes) {
    Object.assign(nextSettings, settings.custom_attributes);
  }

  return nextSettings;
};

export const getIntercomAppId = () => getAppId();

export const bootIntercom = async (settings = {}) => {
  const nextSettings = buildSettings(settings);
  const win = getWindow();
  if (!win || !nextSettings) return false;

  const nextSignature = buildSignature(nextSettings);
  if (isReady && isBooted) {
    if (lastBootSignature !== nextSignature) {
      win.intercomSettings = nextSettings;
      win.Intercom("update", nextSettings);
      lastBootSignature = nextSignature;
    }
    return true;
  }
  if (bootPromise) {
    const ready = await bootPromise;
    if (ready && lastBootSignature !== nextSignature) {
      updateIntercom(nextSettings);
    }
    return ready;
  }

  const currentBootPromise = (async () => {
    const generation = ++bootGeneration;
    await loadScript(nextSettings.app_id);
    if (generation !== bootGeneration) return false;
    if (typeof win.Intercom !== "function") return false;

    // A boot attempt that timed out must start a fresh Messenger session.
    if (isBooted) win.Intercom("shutdown");
    isBooted = false;
    isReady = false;
    win.intercomSettings = nextSettings;

    return new Promise((resolve) => {
      const timer = win.setTimeout(() => {
        if (generation === bootGeneration) bootGeneration += 1;
        resolve(false);
      }, READY_TIMEOUT_MS);
      win.Intercom("ready", () => {
        if (generation !== bootGeneration) return;
        win.clearTimeout(timer);
        isReady = true;
        resolve(true);
      });
      win.Intercom("boot", nextSettings);
      isBooted = true;
      lastBootSignature = nextSignature;
      win.Intercom("hideNotifications", true);
    });
  })();
  bootPromise = currentBootPromise;

  try {
    return await currentBootPromise;
  } finally {
    if (bootPromise === currentBootPromise) bootPromise = null;
  }
};

export const updateIntercom = async (settings = {}) => {
  const nextSettings = buildSettings(settings);
  const win = getWindow();
  if (!win || !nextSettings || !isReady || !isBooted) return false;

  win.intercomSettings = {
    ...(win.intercomSettings || {}),
    ...nextSettings,
  };
  win.Intercom("update", nextSettings);
  win.Intercom("hideNotifications", true);
  lastBootSignature = buildSignature(win.intercomSettings);
  return true;
};

const clearIntercomCookies = () => {
  const win = getWindow();
  const doc = getDocument();
  if (!win || !doc) return;

  const past = "Thu, 01 Jan 1970 00:00:00 GMT";
  const host = win.location?.hostname || "";
  const domains = [null];

  if (host.includes(".")) {
    const parts = host.split(".");
    domains.push(host);
    domains.push(parts.slice(-2).join("."));
  }

  doc.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0]?.trim();
    if (!name?.startsWith("intercom-")) return;

    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : "";
      doc.cookie = `${name}=; expires=${past}; path=/${domainPart}`;
    });
  });
};

export const shutdownIntercom = () => {
  const win = getWindow();
  if (!win) return false;

  bootGeneration += 1;
  bootPromise = null;
  if (isBooted && typeof win.Intercom === "function") {
    win.Intercom("shutdown");
  }
  clearIntercomCookies();
  isBooted = false;
  isReady = false;
  lastBootSignature = null;
  return true;
};

const callIntercom = (method, ...args) => {
  const win = getWindow();
  if (!win || !isReady || !isBooted || typeof win.Intercom !== "function")
    return false;

  win.Intercom(method, ...args);
  return true;
};

export const showIntercom = () => callIntercom("show");
export const showMessages = () => callIntercom("showMessages");
export const showSpace = (spaceName) => callIntercom("showSpace", spaceName);
export const showNewMessage = (message) =>
  message
    ? callIntercom("showNewMessage", message)
    : callIntercom("showNewMessage");
export const startConversation = (message) =>
  message ? callIntercom("startConversation", message) : false;
export const sendIntercomMessage = (message) => {
  const normalizedMessage = typeof message === "string" ? message.trim() : "";
  if (!normalizedMessage) return false;

  // Open the Messages space first so mobile/PWA lands directly in the active
  // conversation area before we send. This avoids a race where the widget
  // opens to Home and the typing state never renders on smaller viewports.
  const opened = callIntercom("showMessages");
  if (!opened) return false;

  const schedule =
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame.bind(window)
      : (cb) => setTimeout(cb, 0);

  schedule(() => {
    callIntercom("startConversation", normalizedMessage);
  });

  return true;
};
export const showArticle = (articleId) =>
  callIntercom("showArticle", articleId);
export const onUnreadCountChange = (handler) =>
  callIntercom("onUnreadCountChange", handler);
export const trackIntercomEvent = (eventName, metadata) =>
  metadata
    ? callIntercom("trackEvent", eventName, metadata)
    : callIntercom("trackEvent", eventName);
